import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  createSpreadsheetMetricModelForDataActions, getColumnName,
} from '~/_shared/utils/metric/defaultMetric.helpers';
import { useDefaultMetrics } from '~/_shared/utils/metric/defaultMetric.hook';
import {
  mapMetricsToServerModel, type MetricModel,
} from '~/_shared/utils/metric/metrics.factory';
import { areMetricsTheSame } from '~/_shared/utils/metric/metrics.helpers';
import { MetricsDataAction } from '~/_shared/utils/metric/metrics.types';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { isLocationCount } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.helpers';
import { useGetColumnATOMetrics } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/useGetColumnATOMetrics';
import {
  type CreateOptimizedTerritoriesCountParams, useNumberOfTerritoriesCount,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/useNumberOfTerritoriesCount';
import {
  type DatasetOptimizedTerritoriesResponse, GeoUnits, getDatasetOptimizedTerritories,
} from '~/store/boundaries/boundaries.repository';
import {
  useBoundaryGroupsSelector, useCustomBoundaryGroupsSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryCreateWMSOptimizedAddItem } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimized.actionCreators';
import { INITIAL_BOUNDARY_CREATE_WMS_OPTIMIZED_STATUS } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimized.state';
import {
  boundarySelectEditCloseModal, boundarySelectSetSelectType, boundarySelectWMSOptimizedTerritoriesSubsetOpenModal,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import {
  type BoundarySelectState, BoundarySelectType,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import {
  useMatchupDataSelector, useSpreadsheetMatchupDataSelector,
} from '~/store/matchupData/matchupData.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { usePrimarySpreadsheetFilterTreeItem } from '~/store/spreadsheetData/filtering/usePrimarySpreadsheetFilterTreeItemRequest';
import { CreateBoundaryGroupFromAIModalComponent } from './createBoundaryGroupFromAIModal.component';
import {
  convertGeoUnitToBoundaryGroupId, getTargetNumberOfTerritories, getUSBoundaryGroupIds, populateWMSColumnValues,
  populateWMSDemographicValues,
} from './createBoundaryGroupFromAIModal.helpers';
import {
  BoundaryGroupFromAIModalSteps, BuildingBlockSelection, BuildingBlockSubsetType, type CreateBoundaryGroupFromAIConfig,
} from './createBoundaryGroupFromAIModal.types';
import { useWMSConstraintsValidation } from './modalSteps/constraintsValidation/useWMSConstraintsValidation';
import { useGetDatasetBoundariesOptimizedTerritories } from './useDatasetBoundariesOptimizedTerritories';
import { useGetDatasetSpreadsheetOptimizedTerritories } from './useDatasetSpreadsheetOptimizedTerritories';
import { useGenerateWMSOptimizedTerritories } from './useGenerateWMSOptimizedTerritories';

const createEmptyConfiguration: () => CreateBoundaryGroupFromAIConfig = () => ({
  allStates: false,
  applyFilterOnData: false,
  buildingBlock: null,
  buildingBlockSelection: BuildingBlockSelection.ALL,
  buildingBlockSubsetType: BuildingBlockSubsetType.ON_MAP,
  dataMetricsValues: [],
  demographicsValues: [],
  groupName: '',
  numberOfPreviousTerritories: null,
  numberOfSalesReps: null,
  numberOfTerritories: null,
  optimizationConstraints: false,
  overrideTerritory: false,
  previouslySavedBoundaryGroupId: null,
  respectPrevTerritories: false,
  respectSalesLocations: false,
  salesRepLocationsMap: null,
  salesRepNameColumnId: null,
  selectedBoundaryIds: [],
  useNumberOfTerritories: false,
});

export type CreateBoundaryGroupFromAIModalContainerProps = ModalProps<{
  initialConfig?: CreateBoundaryGroupFromAIConfig;
  initialStep?: BoundaryGroupFromAIModalSteps;
}>;

export const CreateBoundaryGroupFromAIModalContainer: FC<CreateBoundaryGroupFromAIModalContainerProps> = ({
  initialConfig,
  initialStep,
  isOpen,
  onClose,
}: CreateBoundaryGroupFromAIModalContainerProps) => {
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const { openModal: openBoundaryCreateGroupFromAIModal } = useModal('CreateBoundaryGroupFromAI');
  const { createOptimizedTerritoriesRequest } = useGenerateWMSOptimizedTerritories();
  const { getDatasetBoundariesOptimizedTerritoriesRequest } = useGetDatasetBoundariesOptimizedTerritories();
  const { getDatasetSpreadsheetOptimizedTerritoriesRequest } = useGetDatasetSpreadsheetOptimizedTerritories();
  const spreadsheetId = usePrimarySpreadsheetId();
  const boundariesGroups = useBoundaryGroupsSelector();
  const { columnMetrics, isLoading: isLoadingColumnsInfo } = useGetColumnATOMetrics(spreadsheetId, { includeGroups: true });
  const spreadsheetMatchupData = useSpreadsheetMatchupDataSelector(spreadsheetId);
  const filter = usePrimarySpreadsheetFilterTreeItem();
  const usBoundaryGroupsIds = useMemo(() => getUSBoundaryGroupIds(boundariesGroups), [boundariesGroups]);
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const { computeDefaultSpreadsheetColumnMetrics } = useDefaultMetrics();
  const matchupData = useMatchupDataSelector();
  const [isGeneratingOptimizedTerritories, setIsGeneratingOptimizedTerritories] = useState(false);
  const [isLoadingSubsetBlocks, setIsLoadingSubsetBlocks] = useState(false);
  const [currentStep, setCurrentStep] = useState<BoundaryGroupFromAIModalSteps>(initialStep ?? BoundaryGroupFromAIModalSteps.BuildingBlock);
  const [config, setConfig] = useState<CreateBoundaryGroupFromAIConfig>({ ...createEmptyConfiguration(), ...initialConfig });
  const [optimizedTerritoriesRequestError, setOptimizedTerritoriesRequestError] = useState<string | null>(null);

  const boundaryGroupId = convertGeoUnitToBoundaryGroupId(config.buildingBlock, usBoundaryGroupsIds);

  const countOfTerritoriesConfig: CreateOptimizedTerritoriesCountParams = useMemo(() => ({
    numberOfPreviousTerritories: config.numberOfPreviousTerritories,
    numberOfSalesReps: config.numberOfSalesReps,
    previousTerritoriesBoundaryGroupId: config.previouslySavedBoundaryGroupId,
    respectPrevTerritories: config.respectPrevTerritories,
    respectSalesLocations: config.respectSalesLocations,
    salesRepLocationsMap: config.salesRepLocationsMap,
    useNumberOfTerritories: config.useNumberOfTerritories,
  }), [config.numberOfPreviousTerritories, config.numberOfSalesReps, config.previouslySavedBoundaryGroupId,
    config.respectPrevTerritories, config.respectSalesLocations, config.salesRepLocationsMap, config.useNumberOfTerritories]);

  const constraintValidationState = useWMSConstraintsValidation({
    boundaryGroupId,
    constraintsEnabled: config.optimizationConstraints,
    dataMetricsValues: config.dataMetricsValues,
    demographicsValues: config.demographicsValues,
    isStepActive: currentStep === BoundaryGroupFromAIModalSteps.AdditionalOptions,
    statesSubset: config.statesSubset,
    zipCodesSubset: config.zipCodesSubset,
  });

  const onNumberOfSalesRepChange = useCallback((numberOfSalesReps: number | null) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      numberOfSalesReps,
    }));
  }, []);

  const onNumberOfTerritoriesChange = useCallback((numberOfTerritories: number | null) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      numberOfTerritories,
    }));
  }, []);

  const {
    numberOfTerritoriesUpdateReason, onRefreshTerritoriesCount, handleNumberOfTerritoriesChange, numberOfTerritoriesLoading,
  } = useNumberOfTerritoriesCount({ config: countOfTerritoriesConfig, onNumberOfTerritoriesChange, onNumberOfSalesRepChange });

  const onGoToStep = useCallback((step: BoundaryGroupFromAIModalSteps) => {
    setCurrentStep(step);
  }, []);

  useEffect(() => {
    setConfig(previous => ({
      ...previous,
      dataMetricsValues: columnMetrics.map(populateWMSColumnValues),
    }));
  }, [columnMetrics]);

  useEffect(() => {
    setConfig(previous => ({
      ...previous,
      demographicsValues: populateWMSDemographicValues(t),
    }));
  }, [t]);

  useEffect(() => {
    const getSubsetsForWMS = async () => {
      try {
        setIsLoadingSubsetBlocks(true);
        if (clientId === null || !config.selectedBoundaryIds.length) {
          return;
        }
        const wmsCodes = await getDatasetOptimizedTerritories(clientId, {
          ...(config.buildingBlock === GeoUnits.US_STATES ? { states: config.selectedBoundaryIds } : {}),
          ...(config.buildingBlock === GeoUnits.US_ZIP_CODES ? { zips: config.selectedBoundaryIds } : {}),
        });

        setConfig((prevState) => ({
          ...prevState,
          statesSubset: [...(wmsCodes.data.dataset_states ? [...wmsCodes.data.dataset_states] : [])],
          zipCodesSubset: [...(wmsCodes.data.dataset_zip_codes ? [...wmsCodes.data.dataset_zip_codes] : [])],
        }));
      }
      catch {
        console.error('Error during fetching codes for wms');
      }
      finally {
        setIsLoadingSubsetBlocks(false);
      }
    };
    getSubsetsForWMS();
  }, [config.selectedBoundaryIds, config.buildingBlock, clientId]);

  const onSelectSubsetFromMap = useCallback(() => {
    onClose();
    dispatch(boundarySelectSetSelectType(BoundarySelectType.Lasso));
    dispatch(boundarySelectWMSOptimizedTerritoriesSubsetOpenModal({
      boundaryGroupId,
      selectedBoundaryIds: config.selectedBoundaryIds,
      configuration: config,
      onClose: () => {
        openBoundaryCreateGroupFromAIModal({
          initialConfig: config,
          initialStep: BoundaryGroupFromAIModalSteps.BuildingBlock,
        });
        dispatch(boundarySelectEditCloseModal());
      },
      onSubmit: (boundarySelect: BoundarySelectState) => {
        if (!boundarySelect?.selectedBoundaryInfos?.length) {
          return;
        }

        openBoundaryCreateGroupFromAIModal({
          initialConfig: {
            ...config,
            selectedBoundaryIds: boundarySelect.selectedBoundaryInfos.map(boundaryInfo => boundaryInfo.boundaryId),
          },
          initialStep: BoundaryGroupFromAIModalSteps.BuildingBlock,
        });
        dispatch(boundarySelectEditCloseModal());
      },
    }));
  }, [onClose, dispatch, config, boundaryGroupId, openBoundaryCreateGroupFromAIModal]
  );

  const onChange = useCallback((update: Partial<CreateBoundaryGroupFromAIConfig>) => {
    setOptimizedTerritoriesRequestError(null);
    setConfig(previous => ({
      ...previous,
      ...update,
    }));

    handleNumberOfTerritoriesChange(update);
  }, [handleNumberOfTerritoriesChange]);

  const onCloseOptimizedTerritoriesRequestError = useCallback(() => {
    setOptimizedTerritoriesRequestError(null);
  }, []);

  const zipStatesColumns = useMemo<{
    zipColumn: string | null;
    stateColumn: string | null;
  }>(() => {
    if (spreadsheetMatchupData === null) {
      return {
        zipColumn: null,
        stateColumn: null,
      };
    }
    return {
      zipColumn: spreadsheetMatchupData.categories.zip.match,
      stateColumn: spreadsheetMatchupData.categories.state.match,
    };
  }, [spreadsheetMatchupData]);

  const onSubmit = useCallback(async () => {
    if (clientId && mapId && spreadsheetId && config.buildingBlock) {
      setOptimizedTerritoriesRequestError(null);
      setIsGeneratingOptimizedTerritories(true);
      let datasetResponse: DatasetOptimizedTerritoriesResponse | null = null;
      try {
        if (config.buildingBlockSelection === BuildingBlockSelection.SUBSET) {
          if (config.buildingBlockSubsetType === BuildingBlockSubsetType.FROM_DATA) {
            datasetResponse = await getDatasetSpreadsheetOptimizedTerritoriesRequest({
              clientId,
              spreadsheetId,
              zips: config.buildingBlock === GeoUnits.US_ZIP_CODES ? zipStatesColumns.zipColumn ?? undefined : undefined,
              states: config.buildingBlock === GeoUnits.US_STATES ? zipStatesColumns.stateColumn ?? undefined : undefined,
            });
          }
          else {
            datasetResponse = await getDatasetBoundariesOptimizedTerritoriesRequest({
              clientId,
              zips: config.buildingBlock === GeoUnits.US_ZIP_CODES ? config.selectedBoundaryIds : undefined,
              states: config.buildingBlock === GeoUnits.US_STATES ? config.selectedBoundaryIds : undefined,
            });
          }
        }

        const defaultSpreadsheetColumnMetrics = await computeDefaultSpreadsheetColumnMetrics();

        const creationParametersMetrics: MetricModel[] = [];

        const processedGroupColumnIds = new Set<string>();

        config.dataMetricsValues.forEach((metric) => {
          if (!metric.checked) {
            return;
          }

          // Skip total location count
          if (isLocationCount(metric.id)) {
            return;
          }

          // Group metrics: send as just a column
          if (notNullsy(metric.groupId)) {
            if (processedGroupColumnIds.has(metric.id)) {
              return;
            }
            processedGroupColumnIds.add(metric.id);
            const columnName = getColumnName(matchupData, spreadsheetId, metric.id);
            creationParametersMetrics.push(...createSpreadsheetMetricModelForDataActions(
              metric.id,
              spreadsheetId,
              columnName,
              [MetricsDataAction.GROUP_COUNT]
            ));
            return;
          }

          creationParametersMetrics.push(...createSpreadsheetMetricModelForDataActions(
            metric.id,
            spreadsheetId,
            metric.name,
            [MetricsDataAction.HIGH_VALUE, MetricsDataAction.LOW_VALUE, MetricsDataAction.SUM, MetricsDataAction.AVERAGE]
          ));
        });

        const filteredDefaultMetric = [...defaultSpreadsheetColumnMetrics].filter((defaultMetric) => {
          return !creationParametersMetrics.some((creationMetric) => areMetricsTheSame(defaultMetric, creationMetric));
        });

        const combinedMetrics = [...creationParametersMetrics, ...filteredDefaultMetric];

        const serverModelMetrics = mapMetricsToServerModel(combinedMetrics);

        const response = await createOptimizedTerritoriesRequest({
          clientId,
          mapId,
          displayName: config.groupName,
          spreadsheetId,
          targetTerritoryCount: getTargetNumberOfTerritories(config) ?? undefined,
          dataMetricsValues: config.dataMetricsValues,
          demographicsValues: config.demographicsValues,
          geounitId: config.buildingBlock,
          respectTerritoryModel: config.respectPrevTerritories ? config.previouslySavedBoundaryGroupId ?? undefined : undefined,
          datasetZipCodes: config.buildingBlock === GeoUnits.US_ZIP_CODES ? datasetResponse?.data?.dataset_zip_codes : undefined,
          datasetStates: config.buildingBlock === GeoUnits.US_STATES ? datasetResponse?.data?.dataset_states : undefined,
          salesRepLocationsMap: (config.respectSalesLocations && config.salesRepLocationsMap && config.salesRepNameColumnId) ? {
            mapId: config.salesRepLocationsMap.mapId,
            nameColumnId: config.salesRepNameColumnId,
          } : undefined,
          filter: config.applyFilterOnData ? filter ?? null : null,
          allStates: config.allStates,
          settings: {
            metrics: serverModelMetrics,
          },
          useConstraints: config.optimizationConstraints,
        });
        dispatch(boundaryCreateWMSOptimizedAddItem({
          type: 'WMS',
          isFailed: false,
          id: response.data.id,
          status: INITIAL_BOUNDARY_CREATE_WMS_OPTIMIZED_STATUS,
          startedOnCurrentTab: true,
        }));
        onClose();
      }
      catch (e) {
        setOptimizedTerritoriesRequestError(e.message);
      }
      finally {
        setIsGeneratingOptimizedTerritories(false);
      }
    }
  }, [clientId, mapId, spreadsheetId, config, filter, createOptimizedTerritoriesRequest, dispatch, onClose,
    getDatasetSpreadsheetOptimizedTerritoriesRequest, zipStatesColumns.zipColumn, zipStatesColumns.stateColumn,
    getDatasetBoundariesOptimizedTerritoriesRequest, computeDefaultSpreadsheetColumnMetrics, matchupData]);

  return (
    <CreateBoundaryGroupFromAIModalComponent
      config={config}
      constraintValidationState={constraintValidationState}
      currentStep={currentStep}
      customBoundaryGroups={customBoundaryGroups}
      isGenerateOptimizedTerritoriesLoading={isGeneratingOptimizedTerritories}
      isLoadingDataMetrics={isLoadingColumnsInfo}
      isLoadingSubsetBlocks={isLoadingSubsetBlocks}
      isOpen={isOpen}
      numberOfTerritoriesLoading={numberOfTerritoriesLoading}
      numberOfTerritoriesUpdateReason={numberOfTerritoriesUpdateReason}
      onChange={onChange}
      onClose={onClose}
      onCloseOptimizedTerritoriesRequestError={onCloseOptimizedTerritoriesRequestError}
      onGoToStep={onGoToStep}
      onRefreshTerritoriesCount={onRefreshTerritoriesCount}
      onSelectSubsetFromMap={onSelectSubsetFromMap}
      onSubmit={onSubmit}
      optimizedTerritoriesRequestError={optimizedTerritoriesRequestError}
      markerFilterActive={!!filter}
    />
  );
};
